import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Zoom,
} from "@mui/material";
import { ArrowDropDownCircleRounded } from "@mui/icons-material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { fixedDrawerStyles } from "./styles";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import { useThemeMode } from "../../../../context/ThemeContext";
import lxlogowhitegreen from "../../../../assets/lxlogowhitegreen.png";
import MuiSVGIcon from "../../../IconPicker/MuiSVGIcon";

export default function FixedDrawer(props) {
  const { appBarHeight, navLinks, sideNavWidth, stickyNavLinks } = props;
  const classes = fixedDrawerStyles({ sideNavWidth, appBarHeight });
  const history = useHistory();
  const { mode, toggleTheme, isDark } = useThemeMode();
  const isActive = (path) => {
    return history.location.pathname.includes(path);
  };

  const recentAssets = useSelector((state) => state.recents.assets);
  const recentInvAssets = useSelector((state) => state.recents.inventoryAssets);
  const recentFacilities = useSelector((state) => state.recents.facilities);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openRecentsDropdown, setOpenRecentsDropdown] = useState(false);
  const [pagesRecents, setPagesRecents] = useState([]);
  const [currPageName, setCurrPageName] = useState("");

  function handleDropdownClose() {
    setAnchorEl(null);
    setOpenRecentsDropdown(false);
    setPagesRecents([]);
  }

  function handleRecentItemClick(itemInfo) {
    if (currPageName === "assets" || currPageName === "inventory") {
      history.push(`/assetSnapshot/${itemInfo}`);
    } else if (currPageName === "facilities") {
      history.push(`/facilities/overview`, { selectedFacility: itemInfo });
    }
    handleDropdownClose();
  }

  // Style for active list items
  const getListItemSx = (linkName) => ({
    ...classes.listItem,
    ...(isActive(linkName) && classes.listItemActive),
  });

  return (
    <Drawer variant="permanent" sx={classes.drawer}>
      <List sx={{ padding: 0 }}>
        {/* Logo */}
        <Tooltip title="Dashboard" placement="right" arrow TransitionComponent={Zoom}>
          <ListItem
            className="pointer"
            key="logo-nav-item"
            onClick={() => history.push("/")}
            sx={classes.menuIcon}
            tabIndex={0}
          >
            <img
              alt="locatorx company logo"
              src={lxlogowhitegreen}
              height="100%"
              style={{ 
                maxHeight: "36px",
                filter: isDark 
                  ? "brightness(1.2) drop-shadow(0 0 10px rgba(160, 196, 109, 0.9)) drop-shadow(0 0 4px rgba(160, 196, 109, 0.8))"
                  : "none",
              }}
            />
          </ListItem>
        </Tooltip>

        {/* Navigation Links */}
        {navLinks.map((link, idx) => {
          const hasDropdown = link.name === "assets" || link.name === "inventory" || link.name === "facilities";
          
          return (
            <Tooltip
              key={`${link.name}-${idx}`}
              title={link.label}
              placement="right"
              arrow
              TransitionComponent={Zoom}
              enterDelay={400}
            >
              <ListItem
                sx={getListItemSx(link.name)}
                tabIndex={0}
              >
                {/* Main clickable area - always centered */}
                <Box
                  onClick={() => {
                    if (link.hyperlink) {
                      window.open(link.hyperlink, "_blank", "noopener,noreferrer");
                    } else {
                      history.push(`/${link.name}`);
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {/* Icon Box */}
                  <Box className="nav-icon-box" sx={classes.navIconBox}>
                    <MuiSVGIcon
                      icon={link.icon}
                      sx={classes.listItemIcon}
                    />
                  </Box>
                  
                  {/* Label */}
                  <ListItemText
                    primary={link.label}
                    sx={classes.listItemText}
                  />
                </Box>

                {/* Dropdown button - positioned absolutely */}
                {hasDropdown && (
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      setAnchorEl(event.currentTarget);
                      setOpenRecentsDropdown(true);
                      if (link.name === "assets") {
                        setPagesRecents(recentAssets);
                        setCurrPageName("assets");
                      } else if (link.name === "inventory") {
                        setPagesRecents(recentInvAssets);
                        setCurrPageName("inventory");
                      } else if (link.name === "facilities") {
                        setPagesRecents(recentFacilities);
                        setCurrPageName("facilities");
                      }
                    }}
                    size="small"
                    sx={classes.dropdownButton}
                  >
                    <ArrowDropDownCircleRounded />
                  </IconButton>
                )}
              </ListItem>
            </Tooltip>
          );
        })}

        {/* Theme Toggle - below Settings */}
        <Tooltip
          title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          placement="right"
          arrow
          TransitionComponent={Zoom}
          enterDelay={400}
        >
          <ListItem
            onClick={toggleTheme}
            sx={classes.listItem}
            tabIndex={0}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                cursor: "pointer",
              }}
            >
              <Box className="nav-icon-box" sx={classes.navIconBox}>
                {isDark ? (
                  <DarkModeIcon sx={{ ...classes.listItemIcon, color: "#A0C46D" }} />
                ) : (
                  <LightModeIcon sx={{ ...classes.listItemIcon, color: "#fbbf24" }} />
                )}
              </Box>
              <ListItemText
                primary={isDark ? "Dark" : "Light"}
                sx={classes.listItemText}
              />
            </Box>
          </ListItem>
        </Tooltip>

        <Box sx={classes.bottomPush} />

        {/* Sticky Navigation Links */}
        {stickyNavLinks.map((link, idx) => (
          <Tooltip
            key={`sticky-${link.name}-${idx}`}
            title={link.label}
            placement="right"
            arrow
            TransitionComponent={Zoom}
            enterDelay={400}
          >
            <ListItem
              onClick={() => {
                if (link.hyperlink) {
                  window.open(link.hyperlink, "_blank", "noopener,noreferrer");
                } else {
                  history.push(`/${link.name}`);
                }
              }}
              sx={getListItemSx(link.name)}
              tabIndex={0}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Box className="nav-icon-box" sx={classes.navIconBox}>
                  <MuiSVGIcon
                    icon={link.icon}
                    sx={classes.listItemIcon}
                  />
                </Box>
                <ListItemText
                  primary={link.label}
                  sx={classes.listItemText}
                />
              </Box>
            </ListItem>
          </Tooltip>
        ))}
      </List>

      {/* Recents Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        onClose={handleDropdownClose}
        open={openRecentsDropdown}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{ sx: classes.menuPaper }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        <Typography sx={classes.recentlyViewedTitle}>
          Recently Viewed
        </Typography>
        {pagesRecents.length > 0 ? (
          pagesRecents.map((recent, index) => (
            <MenuItem
              key={index}
              onClick={() => handleRecentItemClick(recent.assetId || recent.facilityData)}
              sx={classes.menuItem}
            >
              <Box
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#A0C46D",
                  marginRight: "12px",
                  boxShadow: "0 0 6px rgba(160, 196, 109, 0.5)",
                }}
              />
              {recent.assetTag || recent.facilityName}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled sx={{ ...classes.menuItem, opacity: 0.5, fontStyle: "italic" }}>
            No recent items
          </MenuItem>
        )}
      </Menu>
    </Drawer>
  );
}
